"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Phone, Mail, Calendar, Ship, Wrench, Check, Anchor, Hash } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const contactInfo = {
  phone: "+1 (773) 410-1234",
  email: "info@boatbathers.com",
  address: "Slip 42, Harbor View Marina, Coastal City"
};
export default function ServiceRequest() {
  return <section data-section-id="28322" id="secure-your-quote" className="bg-secondary text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {}
          <div className="lg:col-span-4 flex flex-col gap-8">
            <div>
              <h2 className="text-4xl font-serif tracking-tighter text-primary mb-4">
                Marine Concierge
              </h2>
              <p className="text-muted-foreground text-lg font-default">
                Speak with our precision detailing specialists to restore and protect your vessel's showroom finish.
              </p>
            </div>

            <div className="grid gap-6">
              <Link to={`tel:${contactInfo.phone.replace(/\D/g, "")}`} className="block group">
                <Card className="h-full transition-all duration-300 hover:shadow-xl hover:border-ring/30 border-border/40 bg-card rounded-lg shadow-md">
                  <CardHeader className="pb-2">
                    <div className="mb-2 w-12 h-12 rounded-lg bg-ring/10 flex items-center justify-center text-ring group-hover:bg-ring group-hover:text-primary-foreground transition-colors">
                      <Phone className="w-5 h-5 stroke-[1.5px]" />
                    </div>
                    <CardTitle className="text-xl font-default uppercase tracking-wide">Direct Line</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-1 text-sm">Immediate Service Inquiries</p>
                    <p className="text-lg font-default font-semibold text-foreground group-hover:text-ring transition-colors">{contactInfo.phone}</p>
                  </CardContent>
                </Card>
              </Link>

              <Link to={`mailto:${contactInfo.email}`} className="block group">
                <Card className="h-full transition-all duration-300 hover:shadow-xl hover:border-ring/30 border-border/40 bg-card rounded-lg shadow-md">
                  <CardHeader className="pb-2">
                    <div className="mb-2 w-12 h-12 rounded-lg bg-ring/10 flex items-center justify-center text-ring group-hover:bg-ring group-hover:text-primary-foreground transition-colors">
                      <Mail className="w-5 h-5 stroke-[1.5px]" />
                    </div>
                    <CardTitle className="text-xl font-default uppercase tracking-wide">Digital Inquiry</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-1 text-sm">Custom Detailing Proposals</p>
                    <p className="text-lg font-default font-semibold text-foreground group-hover:text-ring transition-colors">{contactInfo.email}</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Card className="border-none shadow-2xl overflow-hidden rounded-lg bg-card">
              <div className="h-1.5 bg-ring w-full" />
              <CardHeader className="bg-card border-b border-border/30 pb-8">
                <CardTitle className="text-2xl font-serif flex items-center gap-3 text-primary">
                  <Wrench className="w-6 h-6 text-ring stroke-[1.5px]" />
                  Secure Your Quote
                </CardTitle>
                <CardDescription className="font-default">
                  Tell us about your boat and the precision care it requires.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8 bg-card">
                <FormProvider formKey="request_quote_home" formId="request_quote_home" sectionName="reach-out-service-request" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                preferred_date: "",
                vessel_name: "",
                vessel_type: "",
                harbor_name: "",
                slip_number: "",
                services_needed: [],
                additional_details: ""
              }} validationRules={{
                full_name: {
                  required: "Owner Name is required"
                },
                email: {
                  required: "Email Address is required",
                  pattern: "email"
                },
                phone: {
                  required: "Phone Number is required"
                },
                vessel_name: {
                  required: "Vessel Name is required"
                },
                services_needed: {
                  required: "Please select at least one detailing package"
                }
              }} submitText="Request Precision Quote" successMessage="Thank you! Your quote request has been received. Our marine detailing specialists will review your vessel's needs and contact you with a precision estimate shortly.">
                  <ServiceRequestForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}
function ServiceRequestForm() {
  const {
    isSubmitting,
    handleSubmit,
    control
  } = useWvcForm();
  const servicesList = ["Exterior Detailing", "Maintenance Wash", "Interior Deep cleaning", "Waxing Service", "Other"];
  const inputClasses = "bg-background border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-ring/20 focus-visible:border-ring transition-all rounded-lg";
  const labelClasses = "text-muted-foreground font-default uppercase tracking-widest text-[10px]";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Owner Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Owner Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Preferred Service Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="text" placeholder="" {...field} className={inputClasses} />
                  <SafeHydrate><Calendar className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none stroke-[1.5px]" /></SafeHydrate>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="h-px bg-border/20 my-6" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="vessel_name" rules={{
        required: "Vessel Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Vessel Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input placeholder="Sea Breeze" {...field} className={`${inputClasses} pl-10`} />
                  <Ship className="absolute left-3 top-2.5 h-4 w-4 text-ring stroke-[1.5px]" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vessel_type" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Vessel Length</FormLabel>
              <FormControl>
                <Input placeholder="Length" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="harbor_name" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Harbor Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input placeholder="Harbor XYZ" {...field} className={`${inputClasses} pl-10`} />
                  <Anchor className="absolute left-3 top-2.5 h-4 w-4 text-ring stroke-[1.5px]" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="slip_number" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Slip Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input placeholder="12A" {...field} className={`${inputClasses} pl-10`} />
                  <Hash className="absolute left-3 top-2.5 h-4 w-4 text-ring stroke-[1.5px]" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="services_needed" rules={{
      required: "Please select at least one detailing package"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={`${labelClasses} text-xs block mb-4 text-primary`}>Detailing Package</FormLabel>
            <FormDescription className="mb-4 font-default text-xs">
              Select all precision services you require for your vessel.
            </FormDescription>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {servicesList.map((service, i) => {
          const isChecked = field.value?.includes(service);
          return <label key={service} data-index={i} className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all
                    ${isChecked ? "bg-accent/50 border-ring ring-0" : "bg-background border-border hover:border-ring/30 hover:bg-accent/10"}
                  `}>
                    <div className={`
                      flex items-center justify-center w-5 h-5 rounded border transition-colors
                      ${isChecked ? "bg-ring border-ring text-white" : "border-border bg-transparent"}
                    `}>
                      {isChecked && <Check className="w-3.5 h-3.5 stroke-[2px]" />}
                    </div>
                    <input type="checkbox" className="sr-only" checked={isChecked} onChange={e => {
              const checked = e.target.checked;
              const currentValues = field.value || [];
              if (checked) {
                field.onChange([...currentValues, service]);
              } else {
                field.onChange(currentValues.filter((v: string) => v !== service));
              }
            }} />
                    <span className={`text-xs font-default ${isChecked ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                      {service}
                    </span>
                  </label>;
        })}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Additional Details</FormLabel>
            <FormControl>
              <Textarea className={`min-h-[120px] font-default ${inputClasses}`} placeholder="Provide any extra information about the vessel or specific issues..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <Link to="mailto:info@boatbathers.com" className="inline-block w-full md:w-auto">
        <Button type="button" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[240px] h-14 text-sm uppercase tracking-[0.15em] font-default bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl rounded-lg transition-all duration-300">
          {isSubmitting ? "Processing Request..." : "Request Precision Quote"}
        </Button>
      </Link>
    </Form>;
}